import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import { loadChats, saveMessage } from "./chatStorage";
import UserList from "./UserList";
import ChatWindow from "./ChatWindow";
import { hostname } from "../../config";
import { getUser } from "../../Model/LocalStorage";
import { useParams } from "react-router-dom";
import { getApprove, getDecline, requestForApproval } from "../Model/Auction";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const formatAMPM = (date) => {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  return `${hours}:${minutes} ${ampm}`;
};

const ChatSupportPage = () => {
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [conversations, setConversations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  // ✅ Dito naka-store ang unread count: { userId: count }
  const [unreadMessages, setUnreadMessages] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [requests, setRequests] = useState([]); // ✅ list of joiners
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const { auctionID } = useParams();
  // ✅ Gagamitin ito para ma-check kung naka-focus ba ang Admin sa user
  const selectedUserIdRef = useRef(selectedUserId);

  useEffect(() => {
    selectedUserIdRef.current = selectedUserId;
  }, [selectedUserId]);

  // ---------------- MESSAGE HANDLERS ----------------
  const updateConversationsState = useCallback((userId, message) => {
    saveMessage(userId, message);
    setConversations((prevChats) => ({
      ...prevChats,
      [userId]: [...(prevChats[userId] || []), message],
    }));
  }, []);

  const handleSendMessage = useCallback(
    (userId, text) => {
      const trimmedText = text.trim();
      if (!socket || !trimmedText || !userId || !currentUser) return;

      const bidderUser = onlineUsers.find((u) => u.userId === userId);
      const bidderEmail = bidderUser?.email;
      if (!bidderEmail) return;

      const timestamp = Date.now();
      const uniqueId = timestamp + Math.random().toString(36).substring(2);

      const payload = {
        id: uniqueId,
        message: trimmedText,
        receiver: bidderEmail,
        recipientId: userId,
        sender: currentUser.email,
        room: userId,
        senderId: currentUser.id,
        time: formatAMPM(new Date(timestamp)),
        timestamp,
      };

      socket.emit("send_message", payload);
    },
    [socket, currentUser, onlineUsers]
  );

  const handleSelectUser = useCallback((userId) => {
    setSelectedUserId(userId);
    // ✅ NEW: I-clear ang unread count kapag pinili ang user
    setUnreadMessages((prev) => {
      if (prev[userId]) {
        const copy = { ...prev };
        delete copy[userId];
        return copy;
      }
      return prev;
    });
  }, []);

  // ---------------- SOCKET CONNECTION ----------------
  useEffect(() => {
    setConversations(loadChats());

    const setupSocket = async () => {
      const user = await getUser();
      setCurrentUser(user);

      if (!user || user.group !== "ADMIN") {
        setError("Access Denied: Only Admin can access this page.");
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("token");
      const socketInstance = hostname(token);
      setSocket(socketInstance);

      const joinRoom = () => {
        socketInstance.emit("joint_room", auctionID);
      };

      const sendConnectionData = () => {
        socketInstance.emit("ConnectedUser", {
          userId: user.id,
          email: user.email,
          group: user.group,
        });
        socketInstance.emit("requestOnlineUsers");
        joinRoom();
      };

      socketInstance.on("connect", () => {
        setLoading(false);
        setError("");
        sendConnectionData();
      });

      socketInstance.on("reconnect", sendConnectionData);
      socketInstance.on("connect_error", (err) => {
        console.error("Socket error:", err);
        setError("Connection failed. Check your server or internet.");
      });

      socketInstance.on("onlineUsersList", (users) => setOnlineUsers(users));
      
      // ✅ MODIFIED: Handling ng bagong mensahe
      socketInstance.on("receiveMessage", (msg) => {
        const isFromSelf = String(msg.senderId) === String(user.id);
        const chattingWithId = isFromSelf ? msg.recipientId : msg.senderId;
        const bidderId = Number(chattingWithId);

        if (bidderId && !isNaN(bidderId)) {
          updateConversationsState(bidderId, {
            id: msg.id || Date.now(),
            text: msg.text || msg.message,
            sender: isFromSelf ? "support" : "user",
            timestamp: msg.timestamp || Date.now(),
            time: formatAMPM(new Date(Number(msg.timestamp) || Date.now())),
          });

          // ✅ NEW: Mag-increment ng unread count kung galing sa user AT hindi ito ang current chat
          if (!isFromSelf && bidderId !== selectedUserIdRef.current) {
            setUnreadMessages((prevUnread) => ({
              ...prevUnread,
              [bidderId]: (prevUnread[bidderId] || 0) + 1,
            }));
          }
        }
      });

      return () => socketInstance.disconnect();
    };

    setupSocket();
  }, [auctionID, updateConversationsState]);

  // ✅ Kung gusto mong makita sa listahan ang unread count, gagamitin ang sorting logic sa UserList
  const usersForList = useMemo(() => [...onlineUsers].sort((a, b) => a.userId - b.userId), [onlineUsers]);
  const selectedUser = usersForList.find((u) => u.userId === selectedUserId);

  // ---------------- APPROVAL MANAGEMENT ----------------
  // (Walang pagbabago dito, ang buong code ay pareho pa rin)
  const fetchRequests = async () => {
    setLoadingRequests(true);
    try {
      const res = await requestForApproval(auctionID);
      if (res?.sucess) {
        setRequests(res.data || []);
      } else {
        setRequests([]);
      }
    } catch (e) {
      console.error("Failed to fetch requests:", e);
      setRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  const openDialog = () => {
    setDialogOpen(true);
    fetchRequests();
  };

  const handleApprove = async (userId) => {
    try {
      const res = await getApprove(userId);
      setSnackbar({
        open: true,
        message: res.message || "Approved successfully!",
        severity: res.success ? "success" : "error",
      });
      fetchRequests(); // refresh list
    } catch (e) {
      setSnackbar({ open: true, message: "Approval failed.", severity: "error" });
    }
  };

  const handleDecline = async (userId) => {
    try {
      const res = await getDecline(userId);
      setSnackbar({
        open: true,
        message: res.message || "Declined successfully!",
        severity: res.success ? "success" : "error",
      });
      fetchRequests(); // refresh list
    } catch (e) {
      setSnackbar({ open: true, message: "Decline failed.", severity: "error" });
    }
  };

  // ---------------- CONDITIONAL RENDER ----------------
  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Container maxWidth="sm" sx={{ mt: 10, textAlign: "center" }}>
        <Paper elevation={4} sx={{ p: 5, borderRadius: 3 }}>
          <Typography variant="h5" color="error" fontWeight="bold" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please log in with an administrator account to access this page.
          </Typography>
        </Paper>
      </Container>
    );

  // ---------------- MAIN UI ----------------
  return (
    <Container maxWidth="xl" sx={{ mt: 5, mb: 5, height: "90vh" }}>
    <Box sx={{ 
    display: "flex", 
    flexDirection: { xs: "column", sm: "row" }, 
    justifyContent: "space-between", 
    alignItems: { xs: "flex-start", sm: "center" }, 
    gap: 2,
    mb: 3 
  }}>
    <Typography variant="h4" fontWeight={700} sx={{ color: "#1a237e", fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
      Live Chat Support
    </Typography>

    <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
      <Button
        variant="outlined"
        size="small"
        href="/declarator/room"
        sx={{ flex: 1 }}
      >
        Back
      </Button>
      <Button 
        variant="contained" 
        size="small"
        onClick={openDialog} 
        sx={{ flex: 2 }}
      >
        Manage Requests
      </Button>
    </Box>
  </Box>

  <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 0, height: '100%' }}>
    
    
    <Grid item xs={12} md={4} lg={3} sx={{ 
      height: "100%", 
      display: { xs: selectedUserId ? "none" : "block", md: "block" } 
    }}>
      <Paper elevation={4} sx={{ height: "100%", borderRadius: 2, overflow: "hidden", display: 'flex', flexDirection: 'column' }}>
        <UserList
          users={usersForList} // Kapag nagbago ang array na 'to, kusa itong mag-uupdate
          selectedUserId={selectedUserId}
          onSelectUser={handleSelectUser}
          unreadMessages={unreadMessages} 
        />
      </Paper>
    </Grid>

    
    <Grid item xs={12} md={8} lg={9} sx={{ 
      height: "100%", 
      display: { xs: selectedUserId ? "flex" : "none", md: "flex" },
      flexDirection: 'column'
    }}>
      <Paper
        elevation={4}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          bgcolor: '#fff' // Siguradong solid ang background
        }}
      >
        
        <Box sx={{ display: { xs: 'flex', md: 'none' }, p: 1, borderBottom: '1px solid #eee', alignItems: 'center' }}>
             <Button 
                startIcon={<ArrowBackIcon />}
                onClick={() => handleSelectUser(null)}
                size="small"
              >
                Users
              </Button>
              {selectedUser && (
                <Typography sx={{ ml: 2, fontWeight: 'bold', fontSize: '0.9rem' }}>
                   {selectedUser.email?.split('@')[0]}
                </Typography>
              )}
        </Box>

        {selectedUserId ? (
          <ChatWindow
            user={selectedUser}
            // Real-time update: pag may pumasok sa conversation state, dito agad papasok
            messages={conversations[selectedUserId] || []} 
            onSendMessage={handleSendMessage}
          />
        ) : (
          /* Static Welcome Box - Naka-abang lang habang walang chat */
          <Box sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
            <Typography variant="h6" color="text.secondary">Welcome, Admin!</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Select a client to view messages
            </Typography>
          </Box>
        )}
      </Paper>
    </Grid>
</Grid>
      
      <Box
  component="footer"
  sx={{
    py: 2,
    textAlign: "center",
    color: "#4a4a4a",
    fontSize: "0.95rem",
    letterSpacing: "0.5px",
    fontFamily: "'Cormorant Garamond', serif",
    // borderTop: "1px solid #e0e0e0",
  }}
>
  © {new Date().getFullYear()} <strong>Leon Gallery Fine Art & Antiques</strong><br />
  <span style={{ fontSize: "0.85rem", opacity: 0.8 }}>All Rights Reserved</span>
</Box>

      

      
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Joiner Requests (Auction #{auctionID})</DialogTitle>
        <DialogContent dividers>
          {loadingRequests ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Loading joiner list...
              </Typography>
            </Box>
          ) : requests.length > 0 ? (
            <List>
              {requests.map((req, index) => (
                <React.Fragment key={req.id}>
                  <ListItem
                    secondaryAction={
                      <Box>
                        <Button
                          size="small"
                          color="success"
                          variant="contained"
                          onClick={() => handleApprove(req.user_id)}
                          sx={{ mr: 1 }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="contained"
                          onClick={() => handleDecline(req.user_id)}
                        >
                          Decline
                        </Button>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={`${req.firstname} ${req.lastname}`}
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {req.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Status: {req.status}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < requests.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography textAlign="center" color="text.secondary" sx={{ py: 3 }}>
              No joiner requests found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ChatSupportPage;